"use client";

import React, { useMemo } from "react";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, Eye } from "lucide-react";
import { Client } from "@/lib/types/database";
import { TicketSelect } from "@/components/ui/selects";
import { ClientSelectWithModal } from "@/components/ui/selects";
import { previewTemplate } from "@/lib/utils/crm/template-variables";
import { plainTextToHtml } from "@/components/ui/rich-text-editor";
import { TicketSummary } from "@/lib/types/database";
import { useVariableContext } from "../messages/contexts/VariableContext";

interface PanelProps {
  mode?: "panel" | "inline";
  subject: string;
  body: string;
  // Context for variable resolution
  selectedClient?: Client | null;
  onClientSelect?: (clientId: string) => void;
  clientOptions?: Client[];
  clientsLoading?: boolean;
  isSearching?: boolean;
  clientSearchTerm?: string;
  onClientSearchChange?: (term: string) => void;
  // Tickets (optional)
  ticketOptions?: TicketSummary[];
  ticketsLoading?: boolean;
  selectedTicket?: TicketSummary | null;
  onTicketSelect?: (ticketId: string) => void;
  // UI control
  showSelectors?: boolean;
  className?: string;
  // Debug/override: force plaintext rendering even if content looks like HTML
  treatAsPlainText?: boolean;
}

export function TemplatePreview({
  mode = "panel",
  subject,
  body,
  selectedClient,
  onClientSelect,
  clientOptions = [],
  clientsLoading,
  isSearching,
  clientSearchTerm,
  onClientSearchChange,
  ticketOptions = [],
  ticketsLoading,
  selectedTicket,
  onTicketSelect,
  showSelectors = true,
  className,
  treatAsPlainText = false,
}: PanelProps) {
  // Use VariableContext for centralized variable resolution
  const variableContext = useVariableContext();

  const preview = useMemo(() => {
    // Use VariableContext for template resolution
    return variableContext.resolveTemplate(
      subject,
      body || "",
      selectedClient || undefined,
      selectedTicket || undefined
    );
  }, [subject, body, selectedClient, selectedTicket, variableContext]);

  const resolvedBody = useMemo(() => {
    let bodyText = preview?.body ?? body ?? "";
    // Convert literal "\n" strings (backslash + n) to actual newline characters
    bodyText = bodyText.replace(/\\n/g, "\n");
    return bodyText;
  }, [preview?.body, body]);
  const resolvedSubject = useMemo(
    () => preview?.subject ?? subject ?? "",
    [preview?.subject, subject]
  );
  const isLikelyHtml = (s: string) => /<\/?[a-z][\s\S]*>/i.test(s);

  // Normalize paragraph breaks so empty paragraphs and adjacent paragraphs create visible spacing
  // Also convert \n characters to <br> tags for proper line breaks
  const normalizedHtml = useMemo(() => {
    if (!resolvedBody) return resolvedBody;

    let html = resolvedBody;

    // Convert actual newline characters to <br> tags (resolvedBody already converted literal \n strings)
    html = html.replace(/\n/g, "<br>");

    // Then normalize paragraph breaks if it's HTML
    if (isLikelyHtml(html)) {
      html = html.replace(/<\/p>\s*<p>/gi, "<br><br>");
      html = html.replace(/<p>\s*<\/p>/gi, "<br>");
    }

    return html;
  }, [resolvedBody]);

  if (mode === "inline") {
    const hasVariables = (subject || "").includes("{{") || (body || "").includes("{{");
    if (!hasVariables) return null;
    return (
      <div className={className}>
        <div className="border-t pt-4 mt-4">
          <div className="text-sm font-medium text-muted-foreground mb-3">Email Preview</div>
          <div className="space-y-2">
            {resolvedSubject ? (
              <div className="text-sm p-2 bg-muted/30 rounded border whitespace-pre-wrap">
                {preview?.subject || subject}
              </div>
            ) : null}
            <div className="text-sm p-3 bg-muted/30 rounded border max-h-32 overflow-y-auto">
              {resolvedBody ? (
                !treatAsPlainText && isLikelyHtml(resolvedBody) ? (
                  <div
                    className="prose prose-sm max-w-none prose-ul:list-disc prose-ol:list-decimal prose-ul:ml-5 prose-ol:ml-5"
                    dangerouslySetInnerHTML={{ __html: normalizedHtml }}
                  />
                ) : (
                  <div className="whitespace-pre-wrap break-words" style={{ tabSize: 4 as any }}>
                    {resolvedBody}
                  </div>
                )
              ) : (
                <span className="text-muted-foreground italic">No body content</span>
              )}
            </div>
            {preview?.unfilledVariables && preview.unfilledVariables.length > 0 && (
              <div className="p-2 bg-amber-50 border border-amber-200 rounded-md text-xs flex items-start gap-2 dark:bg-amber-950/40 dark:border-amber-800">
                <AlertCircle className="h-3 w-3 text-amber-600 mt-0.5 flex-shrink-0 dark:text-amber-400" />
                <div>
                  <div className="font-medium text-amber-800 dark:text-amber-200">Unfilled Variables</div>
                  <div className="text-amber-700 dark:text-amber-300">{preview.unfilledVariables.join(", ")}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <Eye className="h-4 w-4" />
          <span className="text-sm font-medium">Live Preview</span>
        </div>
        {(subject?.includes("{{") || body?.includes("{{")) && (
          <Badge variant="secondary" className="text-[11px]">
            variables detected
          </Badge>
        )}
      </div>

      {showSelectors && (
        <div className="space-y-3 mb-3">
          <div>
            <Label className="text-sm font-medium">Preview with Client Data</Label>
            <ClientSelectWithModal
              value={selectedClient?.id || ""}
              onValueChange={onClientSelect as any}
              placeholder="Select a client for preview..."
              items={clientOptions}
              disabled={clientsLoading}
              isSearching={isSearching}
              searchTerm={clientSearchTerm}
              onSearchChange={onClientSearchChange as any}
            />
          </div>

          {selectedClient && ticketOptions && ticketOptions.length > 0 && onTicketSelect && (
            <div>
              <Label className="text-sm font-medium">Select Ticket (Optional)</Label>
              <TicketSelect
                value={selectedTicket?.id || ""}
                onValueChange={onTicketSelect}
                items={ticketOptions}
                placeholder="Select a ticket for additional context..."
                disabled={ticketsLoading}
              />
            </div>
          )}
        </div>
      )}

      {showSelectors && <Separator />}

      <div className="mt-3">
        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium text-gray-600 dark:text-gray-400">Subject:</Label>
            <div className="mt-1 p-3 bg-card-50 border rounded-md whitespace-pre-wrap break-words">
              {resolvedSubject || "Enter a subject..."}
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-600 dark:text-gray-400">Body:</Label>
            <div className="mt-1 p-4 bg-card-50 border rounded-md min-h-[200px]">
              {resolvedBody ? (
                !treatAsPlainText && isLikelyHtml(resolvedBody) ? (
                  <div
                    className="prose prose-sm max-w-none prose-ul:list-disc prose-ol:list-decimal prose-ul:ml-5 prose-ol:ml-5"
                    dangerouslySetInnerHTML={{ __html: normalizedHtml }}
                  />
                ) : (
                  <div
                    className="text-sm leading-relaxed whitespace-pre-wrap break-words"
                    style={{ tabSize: 4 as any }}
                  >
                    {resolvedBody}
                  </div>
                )
              ) : (
                <div
                  className="prose prose-sm max-w-none prose-ul:list-disc prose-ol:list-decimal prose-ul:ml-5 prose-ol:ml-5"
                  dangerouslySetInnerHTML={{ __html: "<p>Enter email body content...</p>" }}
                />
              )}
            </div>
          </div>
          {preview?.unfilledVariables && preview.unfilledVariables.length > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-md dark:bg-amber-950/40 dark:border-amber-800">
              <div className="flex items-start space-x-2">
                <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0 dark:text-amber-400" />
                <div>
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Unfilled Variables</p>
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    The following variables don&apos;t have data:{" "}
                    {preview.unfilledVariables.join(", ")}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
